import React, {useEffect, useState, useCallback} from "react";
import {Table, Typography, Spin, message, Select, Row, Col, Modal, Button} from "antd";
import {FilePdfOutlined} from "@ant-design/icons";
import {useMediaQuery} from "react-responsive";
import {motion} from "framer-motion";
import {useSelector} from "react-redux";
import {jsPDF} from "jspdf";
import autoTable from "jspdf-autotable";
import statisticsService from "../services/StatisticsService";
import promoterService from "../services/PromoterService";
import theme from "../theme";
import {QUERY_STATE_ROLE, STATE_ROLE, useUserRole} from "../utils/userSessionUtils";

const {Option} = Select;
const {Title} = Typography;

const formatAddress = (record) => {
    const street = record.street || '';
    const exteriorNumber = record.exteriorNumber || '';
    const interiorNumber = record.interiorNumber || '';
    const neighborhood = record.neighborhood || '';
    const zipCode = record.zipCode || '';
    const number = exteriorNumber && interiorNumber
        ? `#${exteriorNumber}-${interiorNumber}`
        : exteriorNumber ? `#${exteriorNumber}` : interiorNumber ? `#${interiorNumber}` : '';
    return `${street} ${number}, ${neighborhood}, CP ${zipCode}`.trim();
};

const StatisticsPromotersPage = () => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    const [entityOptions, setEntityOptions] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [sectionOptions, setSectionOptions] = useState([]);
    const [selectedSections, setSelectedSections] = useState([]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedSection, setSelectedSection] = useState(null);
    const [modalPromoters, setModalPromoters] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalPage, setModalPage] = useState(1);
    const [modalPageSize, setModalPageSize] = useState(10);
    const [modalTotal, setModalTotal] = useState(0);

    const isMobile = useMediaQuery({maxWidth: 800});
    const auth = useSelector((state) => state.auth || {});
    const user = auth.user || {};
    const role = useUserRole();
    const {entityId, sections = [], route = []} = user;

    const fetchData = useCallback(
        async ({entityIdFilter = entityId, sectionsFilter = null, page = 1, size = 10}) => {
            const finalSectionsFilter = sectionsFilter || (role !== STATE_ROLE && role !== QUERY_STATE_ROLE ? route : sections);
            if (!entityIdFilter) return;
            setLoading(true);
            try {
                const response = await statisticsService.getStatistics(
                    entityIdFilter,
                    null,
                    finalSectionsFilter?.length ? finalSectionsFilter.join(",") : null,
                    page,
                    size
                );
                const {data, meta} = response.data;
                setStats(data);
                setCurrentPage(page);
                setPageSize(size);
                setTotalItems(meta.totalItems);
            } catch (error) {
                console.error("Error fetching statistics:", error);
                message.error("Error al obtener las estadísticas.");
            } finally {
                setLoading(false);
            }
        },
        [sections, route, entityId, role]
    );

    const fetchSections = useCallback(async (entityId) => {
        if (!entityId) return;
        setLoading(true);
        try {
            if (role === STATE_ROLE || role === QUERY_STATE_ROLE) {
                const response = await statisticsService.getStatistics(entityId, null, null, 1, 10000);
                const uniqueSections = [...new Set(response.data.data.map(stat => stat.sectionId))];
                setSectionOptions(uniqueSections.sort((a, b) => a - b));
            } else {
                setSectionOptions(route.sort((a, b) => a - b));
            }
        } catch (error) {
            console.error("Error fetching sections:", error);
            message.error("Error al obtener las secciones.");
        } finally {
            setLoading(false);
        }
    }, [role, route]);

    const fetchEntities = useCallback(() => {
        if (role === STATE_ROLE || role === QUERY_STATE_ROLE) {
            const options = [entityId].map((entity) => ({id: entity, name: `Entidad ${entity}`}));
            setEntityOptions(options);
            if (options.some((o) => o.id === entityId)) setSelectedEntity(entityId);
        }
    }, [role, entityId]);

    useEffect(() => {
        if (role === STATE_ROLE || role === QUERY_STATE_ROLE) fetchEntities();
    }, [role, fetchEntities]);

    useEffect(() => {
        if (selectedEntity) fetchSections(selectedEntity).catch(console.error);
    }, [selectedEntity, fetchSections]);

    useEffect(() => {
        const defaultSections = role !== STATE_ROLE && role !== QUERY_STATE_ROLE ? route : sections;
        fetchData({
            sectionsFilter: selectedSections.length ? selectedSections : defaultSections,
            entityIdFilter: selectedEntity || entityId,
            page: currentPage,
            size: pageSize,
        }).catch(console.error);
    }, [fetchData, selectedSections, selectedEntity, currentPage, pageSize, sections, route, entityId, role]);

    const handleEntityChange = (entityId) => {
        setSelectedEntity(entityId);
        setSelectedSections([]);
    };

    const handleSectionChange = (values) => {
        setSelectedSections(values);
        setCurrentPage(1);
    };

    const handleTableChange = (newPagination) => {
        const defaultSections = role !== STATE_ROLE && role !== QUERY_STATE_ROLE ? route : sections;
        fetchData({
            page: newPagination.current,
            size: newPagination.pageSize,
            sectionsFilter: selectedSections.length ? selectedSections : defaultSections,
            entityIdFilter: role !== STATE_ROLE && role !== QUERY_STATE_ROLE ? entityId : selectedEntity,
        }).catch(console.error);
    };

    const fetchModalPromoters = useCallback(async (sectionId, page, size) => {
        setModalLoading(true);
        try {
            const response = await promoterService.getAllWithPagination(
                {sectionId: {value: [sectionId], operator: "in"}},
                page,
                size
            );
            setModalPromoters(response.data);
            setModalTotal(response.meta.totalItems);
            setModalPage(page);
            setModalPageSize(size);
        } catch (error) {
            console.error("Error fetching promoters:", error);
            message.error("Error al obtener los promotores.");
        } finally {
            setModalLoading(false);
        }
    }, []);

    const handleRowClick = (record) => {
        setSelectedSection(record.sectionId);
        setIsModalVisible(true);
        fetchModalPromoters(record.sectionId, 1, 10);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setSelectedSection(null);
        setModalPromoters([]);
        setModalPage(1);
        setModalTotal(0);
    };

    const handleModalTableChange = (pagination) => {
        fetchModalPromoters(selectedSection, pagination.current, pagination.pageSize);
    };

    const exportModalPDF = async () => {
        message.loading({content: "Generando PDF...", key: "pdf"});
        try {
            const response = await promoterService.getAllWithPagination(
                {sectionId: {value: [selectedSection], operator: "in"}},
                1,
                10000
            );
            const allPromoters = response.data || [];

            if (!allPromoters.length) {
                message.warning({content: "No hay datos para exportar", key: "pdf"});
                return;
            }

            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const title = `P-${selectedSection}`;
            const dateStr = new Date().toLocaleString("es-MX", {dateStyle: "long", timeStyle: "short"});

            const drawHeader = () => {
                doc.setFontSize(14);
                doc.setTextColor(80, 80, 80);
                doc.text(title, pageWidth / 2, 14, {align: "center"});
                doc.setFontSize(9);
                doc.setTextColor(120, 120, 120);
                doc.text(`Generado: ${dateStr}`, pageWidth / 2, 21, {align: "center"});
            };

            drawHeader();

            const rows = allPromoters.map((p) => [
                p.sectionId,
                `${p.name || ""} ${p.firstLastName || ""} ${p.secondLastName || ""}`.trim(),
                formatAddress(p),
                p.phone || "",
            ]);

            autoTable(doc, {
                head: [["Sección", "Nombre completo", "Domicilio", "Teléfono"]],
                body: rows,
                startY: 26,
                margin: {top: 30},
                headStyles: {fillColor: [100, 100, 100], textColor: 255, fontStyle: "bold"},
                alternateRowStyles: {fillColor: [240, 240, 240]},
                styles: {fontSize: 8, cellPadding: 3, textColor: [60, 60, 60]},
                didDrawPage: (data) => {
                    if (data.pageNumber > 1) drawHeader();
                    doc.setFontSize(8);
                    doc.setTextColor(150, 150, 150);
                    doc.text(`Página ${data.pageNumber}`, pageWidth / 2, pageHeight - 8, {align: "center"});
                },
            });

            doc.save(`promotores_seccion_${selectedSection}_${new Date().toISOString().slice(0, 10)}.pdf`);
            message.success({content: "PDF generado correctamente", key: "pdf"});
        } catch (error) {
            console.error("exportModalPDF error:", error);
            message.error({content: "Error al generar PDF", key: "pdf"});
        }
    };

    const columns = [
        {title: "Entidad", dataIndex: "entityId", key: "entityId"},
        {title: "Sección", dataIndex: "sectionId", key: "sectionId"},
        {title: "Meta mínima", dataIndex: "promotersLimit", key: "promotersLimit"},
        {title: "Avance", dataIndex: "totalPromoterCount", key: "totalPromoterCount"},
        {
            title: "Avance (%)",
            key: "progressPercentage",
            render: (record) => `${(record.totalPromoterCount === 0 ? 0 : ((record.totalPromoterCount * 100) / record.promotersLimit)).toFixed(2)}%`,
        },
    ];

    const modalColumns = [
        {title: "Sección", dataIndex: "sectionId", key: "sectionId", width: 90},
        {
            title: "Nombre completo",
            key: "fullName",
            render: (_, record) =>
                `${record.name || ""} ${record.firstLastName || ""} ${record.secondLastName || ""}`.trim(),
        },
        {
            title: "Domicilio",
            key: "address",
            render: (_, record) => formatAddress(record),
        },
        {title: "Teléfono", dataIndex: "phone", key: "phone", width: 130},
    ];

    return (
        <>
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: isMobile ? "100%" : "90%",
                    width: "90vw",
                    margin: "20px auto",
                    background: theme.colors.card,
                    padding: "20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    overflowX: "auto",
                }}
            >
                <div style={{textAlign: "center", marginBottom: "20px"}}>
                    <Title level={2}>📊 Estadísticas de Promotores</Title>
                </div>

                <Row gutter={[16, 16]} style={{marginBottom: "20px"}}>
                    {(role === STATE_ROLE || role === QUERY_STATE_ROLE) && (
                        <Col xs={24}>
                            <label style={{display: "block", marginBottom: "8px", fontWeight: "bold"}}>
                                Entidad:
                            </label>
                            <Select
                                value={selectedEntity}
                                placeholder="Selecciona una entidad"
                                style={{width: "100%"}}
                                onChange={handleEntityChange}
                                disabled={loading}
                            >
                                {entityOptions.map((entity) => (
                                    <Option key={entity.id} value={entity.id}>{entity.name}</Option>
                                ))}
                            </Select>
                        </Col>
                    )}
                    <Col xs={24}>
                        <label style={{display: "block", marginBottom: "8px", fontWeight: "bold"}}>
                            Secciones:
                        </label>
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Selecciona secciones"
                            value={selectedSections}
                            onChange={handleSectionChange}
                            style={{width: "100%"}}
                            disabled={
                                role === STATE_ROLE || role === QUERY_STATE_ROLE
                                    ? loading || !selectedEntity
                                    : false
                            }
                            options={
                                (role === STATE_ROLE || role === QUERY_STATE_ROLE)
                                    ? sectionOptions.map(id => ({label: `Sección ${id}`, value: id}))
                                    : Array.from(new Set(route)).map(id => ({label: `Sección ${id}`, value: id}))
                            }
                        />
                    </Col>
                </Row>

                <Spin spinning={loading}>
                    <Table
                        columns={columns}
                        dataSource={stats.map((stat) => ({
                            key: `${stat.id}-${stat.sectionId}`,
                            ...stat,
                            progressPercentage: ((stat.totalCount || 0) / (stat.goal || 1)) * 100,
                        }))}
                        rowKey="key"
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: totalItems,
                            showSizeChanger: true,
                            pageSizeOptions: ["5", "10", "20"],
                        }}
                        onChange={handleTableChange}
                    />
                </Spin>
            </motion.div>

            <Modal
                title={`Promotores — Sección ${selectedSection}`}
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={
                    <Button
                        type="primary"
                        danger
                        icon={<FilePdfOutlined/>}
                        onClick={exportModalPDF}
                    >
                        Descargar PDF
                    </Button>
                }
                width={isMobile ? "100%" : 800}
                destroyOnClose
            >
                <Spin spinning={modalLoading}>
                    <Table
                        columns={modalColumns}
                        dataSource={modalPromoters.map((p) => ({key: p.id, ...p}))}
                        rowKey="key"
                        pagination={{
                            current: modalPage,
                            pageSize: modalPageSize,
                            total: modalTotal,
                            showSizeChanger: true,
                            pageSizeOptions: ["5", "10", "20"],
                        }}
                        onChange={handleModalTableChange}
                        scroll={{x: true}}
                        size="small"
                    />
                </Spin>
            </Modal>
        </>
    );
};

export default StatisticsPromotersPage;
